import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { RefreshCw, Loader2, CalendarClock, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api/axios'
import { formatDateTime } from '@/lib/dateUtils'

interface SyncConfig {
    enabled: boolean
    syncInterval: number
    syncIntervalUnit: 'minutes' | 'hours' | 'days'
    syncAtTime: string
    syncTimezone: string
    lastRunAt: string | null
    updatedBy: string | null
    updatedAt: string | null
}

/**
 * Manual sync trigger plus the automatic schedule, in the page header so it is reachable
 * from every tab — the button previously lived only on Model Changes, which is not where
 * anyone looks for it.
 *
 * Both the trigger and the schedule need system.admin on the server; so does this page (router.tsx),
 * so the UI does not re-check — the server is the boundary.
 */
export function SyncControls() {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()

    const { data: config } = useQuery<SyncConfig>({
        queryKey: ['ai-sync-config'],
        queryFn: async () => (await api.get('/api/ai/models/sync/config')).data?.config,
    })

    const [enabled, setEnabled] = useState(false)
    const [interval, setIntervalValue] = useState('24')
    const [unit, setUnit] = useState<'minutes' | 'hours' | 'days'>('hours')
    const [atTime, setAtTime] = useState('02:00')
    // Default to the admin's own zone so "02:00" is never ambiguous.
    const [timezone, setTimezone] = useState(() => {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
    })

    // Seed the form from the server once it arrives, and after every save.
    useEffect(() => {
        if (!config) return
        setEnabled(!!config.enabled)
        setIntervalValue(String(config.syncInterval ?? 24))
        setUnit(config.syncIntervalUnit ?? 'hours')
        setAtTime(config.syncAtTime ?? '02:00')
        if (config.syncTimezone) setTimezone(config.syncTimezone)
    }, [config])

    const unitLabels: Record<'minutes' | 'hours' | 'days', string> = {
        minutes: t('aiModels.sync.units.minutes', 'minutes'),
        hours: t('aiModels.sync.units.hours', 'hours'),
        days: t('aiModels.sync.units.days', 'days'),
    }

    const invalidateAll = () => {
        for (const key of ['ai-changes', 'ai-changes-stats', 'ai-sync-logs', 'ai-sync-logs-count', 'ai-models', 'ai-model-preferences', 'ai-scenarios']) {
            queryClient.invalidateQueries({ queryKey: [key] })
        }
    }

    const syncNow = useMutation({
        mutationFn: () => api.post('/api/ai/models/sync'),
        onSuccess: () => {
            invalidateAll()
            toast({ title: t('aiModels.sync.completed', 'Sync completed') })
        },
        onError: (e: any) => toast({
            title: t('aiModels.sync.failed', 'Sync failed'),
            description: e?.response?.status === 403
                ? t('aiModels.sync.adminRequired', 'Admin privileges required')
                : e?.response?.data?.error,
            variant: 'destructive',
        }),
    })

    const saveSchedule = useMutation({
        mutationFn: () => api.put('/api/ai/models/sync/config', {
            enabled,
            syncInterval: Number(interval),
            syncIntervalUnit: unit,
            syncAtTime: atTime,
            syncTimezone: timezone,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ai-sync-config'] })
            toast({
                title: enabled
                    ? t('aiModels.sync.scheduleSaved', 'Schedule saved')
                    : t('aiModels.sync.scheduleDisabled', 'Automatic sync turned off'),
                description: enabled
                    ? (unit === 'days'
                        ? t('aiModels.sync.scheduleSummaryDaily', 'Syncing every {{n}} {{unit}} at {{time}} {{timezone}}.', {
                            n: interval,
                            unit: Number(interval) === 1 ? t('aiModels.sync.units.day', 'day') : unitLabels.days,
                            time: atTime,
                            timezone,
                        })
                        : t('aiModels.sync.scheduleSummary', 'Syncing every {{n}} {{unit}}.', { n: interval, unit: unitLabels[unit] }))
                    : undefined,
            })
        },
        onError: (e: any) => toast({
            title: t('aiModels.sync.scheduleSaveFailed', 'Could not save schedule'),
            description: e?.response?.status === 403
                ? t('aiModels.sync.adminRequired', 'Admin privileges required')
                : e?.response?.data?.error,
            variant: 'destructive',
        }),
    })

    const lastRun = config?.lastRunAt ? formatDateTime(config.lastRunAt) : null
    const dirty = !!config && (
        enabled !== !!config.enabled ||
        Number(interval) !== config.syncInterval ||
        unit !== config.syncIntervalUnit ||
        atTime !== config.syncAtTime ||
        timezone !== config.syncTimezone
    )

    return (
        <div className="rounded-lg border p-4 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <CalendarClock className="h-4 w-4 text-muted-foreground" />
                        <h2 className="text-sm font-medium">{t('aiModels.sync.title', 'Model sync')}</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('aiModels.sync.description', 'Pull the current model catalogue from each enabled provider.')}{' '}
                        {lastRun
                            ? t('aiModels.sync.lastAutomaticRun', 'Last automatic run {{when}}.', { when: lastRun })
                            : t('aiModels.sync.noAutomaticRun', 'No automatic run yet.')}
                    </p>
                </div>
                <Button variant="outline" onClick={() => syncNow.mutate()} disabled={syncNow.isPending}>
                    {syncNow.isPending
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('aiModels.sync.syncing', 'Syncing…')}</>
                        : <><RefreshCw className="h-4 w-4 mr-2" />{t('aiModels.sync.syncNowSentence', 'Sync now')}</>}
                </Button>
            </div>

            <div className="flex flex-wrap items-end gap-4 border-t pt-4">
                <div className="flex items-center gap-2">
                    <Switch id="sync-enabled" checked={enabled} onCheckedChange={setEnabled} />
                    <Label htmlFor="sync-enabled" className="cursor-pointer">{t('aiModels.sync.autoLabel', 'Sync automatically')}</Label>
                </div>

                <div className="flex items-end gap-2">
                    <div>
                        <Label htmlFor="sync-interval" className="text-xs text-muted-foreground">{t('aiModels.sync.everyLabel', 'Every')}</Label>
                        <Input
                            id="sync-interval"
                            type="number"
                            min={1}
                            value={interval}
                            onChange={(e) => setIntervalValue(e.target.value)}
                            disabled={!enabled}
                            className="h-9 w-20 mt-1"
                        />
                    </div>
                    <Select value={unit} onValueChange={(v) => setUnit(v as typeof unit)} disabled={!enabled}>
                        <SelectTrigger className="h-9 w-[130px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="minutes">{unitLabels.minutes}</SelectItem>
                            <SelectItem value="hours">{unitLabels.hours}</SelectItem>
                            <SelectItem value="days">{unitLabels.days}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {unit === 'days' && (
                    <div>
                        <Label htmlFor="sync-at" className="text-xs text-muted-foreground">{t('aiModels.sync.atLabel', 'At')}</Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Input
                                id="sync-at"
                                type="time"
                                value={atTime}
                                onChange={(e) => setAtTime(e.target.value)}
                                disabled={!enabled}
                                className="h-9 w-[110px]"
                            />
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{timezone}</span>
                        </div>
                    </div>
                )}

                <Button onClick={() => saveSchedule.mutate()} disabled={saveSchedule.isPending || !dirty}>
                    {saveSchedule.isPending
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('aiModels.sync.saving', 'Saving…')}</>
                        : <><Check className="h-4 w-4 mr-2" />{t('aiModels.sync.saveSchedule', 'Save schedule')}</>}
                </Button>

                {config?.updatedBy && !dirty && (
                    <span className="text-xs text-muted-foreground pb-2">{t('aiModels.sync.lastChangedBy', 'Last changed by {{user}}', { user: config.updatedBy })}</span>
                )}
            </div>

            {enabled && Number(interval) > 0 && (
                <p className="text-xs text-muted-foreground">
                    {unit === 'days'
                        ? t('aiModels.sync.dstNote', 'Runs at {{time}} {{timezone}}, and stays at that local time across daylight saving changes.', { time: atTime, timezone })
                        : t('aiModels.sync.minimumInterval', 'Minimum interval is 15 minutes.')}{' '}
                    {t('aiModels.sync.backgroundNote', 'The schedule runs in the background and does not need anyone signed in.')}
                </p>
            )}
        </div>
    )
}
